import { ChangeEvent, Ref, SubmitEvent, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { CONNECTED_TURNAROUND_TEST_IDS } from '@connect-x-app/web-test-ids';
import { Button } from '@connect-x-app/web-ui';

import './ChatComposer.scss';

type Props = {
  draft: string;
  isRunning: boolean;
  onDraftChange: (draft: string) => void;
  onSend: (question: string) => void;
  onStop: () => void;
  ref?: Ref<HTMLInputElement>;
};

export const ChatComposer = ({ draft, isRunning, onDraftChange, onSend, onStop, ref }: Props) => {
  const { t } = useTranslation();
  const canSend = !isRunning && draft.trim().length > 0;

  const changeDraft = useCallback((event: ChangeEvent<HTMLInputElement>) => onDraftChange(event.target.value), [onDraftChange]);

  const sendQuestion = useCallback(
    (event: SubmitEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (canSend) {
        onSend(draft);
        onDraftChange('');
      }
    },
    [canSend, draft, onDraftChange, onSend],
  );

  return (
    <form className="chat-composer" onSubmit={sendQuestion}>
      <input
        aria-label={t('CONNECTED_TURNAROUND.PLACEHOLDER')}
        autoFocus
        className="chat-composer__question"
        data-test={CONNECTED_TURNAROUND_TEST_IDS.composer.question}
        onChange={changeDraft}
        placeholder={t('CONNECTED_TURNAROUND.PLACEHOLDER')}
        ref={ref}
        value={draft}
      />
      {isRunning ? (
        <Button isOutline onClick={onStop} testId={CONNECTED_TURNAROUND_TEST_IDS.composer.stop} theme="dark">
          {t('CONNECTED_TURNAROUND.STOP')}
        </Button>
      ) : null}
      <Button disabled={!canSend} testId={CONNECTED_TURNAROUND_TEST_IDS.composer.send} theme="dark" type="submit">
        {t('CONNECTED_TURNAROUND.SEND')}
      </Button>
    </form>
  );
};
